import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/core/api/client";
import type { Capabilities, PuzzleCredit, PuzzleSummary } from "@/core/puzzles/api";
import type { Feedback } from "@/puzzle-types/types";

export interface AttemptView {
  id: number;
  status: "in_progress" | "solved" | "abandoned";
  revealed_at: string;
  submitted_at: string | null;
  solved_at: string | null;
  elapsed_ms: number | null;
  /** Points of the solve, for a `scored` type (ADR 0040); null otherwise. */
  score: number | null;
  attempts_count: number;
  is_ranked: boolean;
  progress_updated_at: string | null;
  server_now: string;
  cooldown_remaining: number;
  /** Whom the attempt belongs to: a team, or `null` for a personal attempt (ADR 0025). */
  team: { id: number; name: string } | null;
}

/** Everything the caller may resume on one puzzle: their own attempt and their teams'. */
export interface MyAttemptsEntry {
  personal: AttemptView | null;
  teams: AttemptView[];
}

/** The attempt of one participant: a team by id, or the personal one when `teamId` is null. */
export function findAttempt(
  entry: MyAttemptsEntry | undefined,
  teamId: number | null,
): AttemptView | null {
  if (!entry) return null;
  if (teamId === null) return entry.personal;
  return entry.teams.find((attempt) => attempt.team?.id === teamId) ?? null;
}

export interface RevealPayload {
  attempt: AttemptView;
  puzzle: PuzzleSummary;
  /** Post-reveal only: the source link would otherwise be playable off the clock. */
  credit: PuzzleCredit | null;
  version: { id: number; number: number; type_version: number };
  definition: unknown;
  rules_text: Record<string, string>;
  constraint_definitions: Record<string, unknown>;
  capabilities: Capabilities;
  progress_state: unknown;
}

export interface SubmitResult {
  correct: boolean;
  feedback: Feedback[];
  attempt: AttemptView;
  next_cooldown_seconds: number;
  rank: number | null;
  score: number | null;
}

export interface LeaderboardEntry {
  rank: number;
  participant: string;
  score: number | null;
  elapsed_ms: number;
  submitted_at: string;
  is_team: boolean;
  is_me: boolean;
}

export function useMyAttempts({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["attempts", "mine"],
    queryFn: () => api<Record<string, MyAttemptsEntry>>("/api/attempts/"),
    enabled,
  });
}

/**
 * What one row of the puzzle list says about the caller's own runs: their best standing on
 * the puzzle. A solve wins over an open run, and a personal run over a team's — the team
 * one is shown when it is all the caller has, since it is the run `reveal` would resume.
 * `null` when the caller never revealed the puzzle.
 */
export function myStanding(entry: MyAttemptsEntry | undefined): AttemptView | null {
  if (!entry) return null;
  const runs = [entry.personal, ...entry.teams].filter((run): run is AttemptView => run !== null);
  return (
    runs.find((run) => run.status === "solved") ??
    runs.find((run) => run.status === "in_progress") ??
    null
  );
}

/**
 * Starts a run and returns the board. Revealing is idempotent — it resumes the run of that
 * kind rather than restarting its clock — so asking for a *fresh* board is a separate verb:
 * `replay: true`, which always opens a new, never-ranked run (ADR 0037).
 */
export function useReveal(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts: { ranked?: boolean; team?: number | null; replay?: boolean } = {}) =>
      api<RevealPayload>(
        opts.replay
          ? `/api/puzzles/${slug}/replay/`
          : `/api/puzzles/${slug}/reveal/${opts.ranked === false ? "?ranked=false" : ""}`,
        { method: "POST", ...(opts.team ? { json: { team: opts.team } } : {}) },
      ),
    onSuccess: (data) => {
      qc.setQueryData(["attempts", data.attempt.id], data);
    },
    // On a refusal too: a tab still offering the ranked reveal after a practice run elsewhere
    // gets `already_practised`, and the refetched list moves it onto that run (ADR 0068).
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["attempts", "mine"] });
    },
  });
}

/**
 * A personal attempt is read once. A team attempt still in progress is pushed over the
 * socket (ADR 0029); while the socket is down it falls back to the poll of ADR 0025: 5 s
 * while visible, 15 s when the tab is hidden, and never once it is solved.
 */
export function useAttempt(id: number | null, { live = false }: { live?: boolean } = {}) {
  return useQuery({
    queryKey: ["attempts", id],
    queryFn: () => api<RevealPayload>(`/api/attempts/${id}/`),
    enabled: id !== null,
    staleTime: Infinity,
    refetchInterval: (query) => {
      if (live) return false;
      const attempt = query.state.data?.attempt;
      if (!attempt?.team || attempt.status !== "in_progress") return false;
      return typeof document !== "undefined" && document.hidden ? 15_000 : 5_000;
    },
    refetchIntervalInBackground: true,
  });
}

export function useSaveProgress(attemptId: number) {
  return useMutation({
    mutationFn: (progress_state: unknown) =>
      api<{ stored: boolean; progress_updated_at: string | null }>(
        `/api/attempts/${attemptId}/progress/`,
        {
          method: "PATCH",
          json: { progress_state, updated_at: new Date().toISOString() },
        },
      ),
  });
}

export function useSubmit(attemptId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (submission: unknown) =>
      api<SubmitResult>(`/api/attempts/${attemptId}/submit/`, {
        method: "POST",
        json: { submission },
      }),
    onSuccess: (result, _vars) => {
      qc.setQueryData<RevealPayload | undefined>(["attempts", attemptId], (old) =>
        old ? { ...old, attempt: result.attempt } : old,
      );
      if (result.correct) {
        void qc.invalidateQueries({ queryKey: ["leaderboard"] });
        void qc.invalidateQueries({ queryKey: ["attempts", "mine"] });
        // The server drops its own 5-minute stats cache on a solve; refresh ours too, or the
        // solver keeps seeing the figures from before they played.
        void qc.invalidateQueries({ queryKey: ["stats"] });
      }
    },
  });
}

/** Polls every 15 s while visible, backs off to 60 s when the tab is hidden. */
/** Pushed over the socket (ADR 0029); polls every 15 s / 60 s hidden while it is down. */
export function useLeaderboard(slug: string, { live = false }: { live?: boolean } = {}) {
  return useQuery({
    queryKey: ["leaderboard", slug],
    queryFn: () =>
      api<{ puzzle: string; entries: LeaderboardEntry[]; count: number; scored: boolean }>(
        `/api/puzzles/${slug}/leaderboard/`,
      ),
    refetchInterval: () => {
      if (live) return false;
      return typeof document !== "undefined" && document.hidden ? 60_000 : 15_000;
    },
    refetchIntervalInBackground: true,
  });
}

export function usePuzzleStats(slug: string) {
  return useQuery({
    queryKey: ["stats", slug],
    queryFn: () =>
      api<{
        started: number;
        solved: number;
        success_rate: number | null;
        median_ms: number | null;
        deciles_ms: number[];
        avg_attempts: number | null;
      }>(`/api/puzzles/${slug}/stats/`),
    staleTime: 5 * 60 * 1000,
  });
}

/** A type's built-in example (ADR 0069): played with no attempt, no clock and no ranking. */
export interface ExamplePayload {
  puzzle_type: string;
  /** The type's display name, from its own `labels`. */
  label: string;
  title: string;
  definition: unknown;
  rules_text: Record<string, string>;
  constraint_definitions: Record<string, unknown>;
  capabilities: Capabilities;
}

export interface ExampleCheckResult {
  correct: boolean;
  feedback: Feedback[];
  score: number | null;
}

const exampleUrl = (puzzleType: string, lang: string) =>
  `/api/puzzle-types/${encodeURIComponent(puzzleType)}/example/?lang=${encodeURIComponent(lang)}`;

export function useExample(puzzleType: string | undefined, lang: string) {
  return useQuery({
    queryKey: ["examples", puzzleType, lang],
    queryFn: () => api<ExamplePayload>(exampleUrl(puzzleType ?? "", lang)),
    enabled: Boolean(puzzleType),
    // A built-in example only changes with a deployment; a type without one stays absent.
    staleTime: Infinity,
    retry: false,
  });
}

export function useExampleCheck(puzzleType: string, lang: string) {
  return useMutation({
    mutationFn: (submission: unknown) =>
      api<ExampleCheckResult>(
        `/api/puzzle-types/${encodeURIComponent(puzzleType)}/example/check/?lang=${encodeURIComponent(lang)}`,
        { method: "POST", json: { submission } },
      ),
  });
}
